import { NavigateFunction } from 'react-router-dom';

import { ensureXpubsAreSaved, getBroadcastsForFingerprint } from ':fat/database/queries';
import { errorToString } from ':fat/helpers/errors';
import { clearAllData, query } from ':fat/messaging/entities/databaseWorker/actions';
import { AppRoutes } from ':fat/routing/routes';

export type HandlerContext = {
  navigate: NavigateFunction;
};

export async function handleMessage(context: HandlerContext, message: TaggedMessage<'mainPage'>) {
  switch (message.kind) {
    case 'PING': {
      void MessageBus.reply(message, { kind: 'PONG' });
      break;
    }
    case 'GO_TO_LANDING': {
      context.navigate(AppRoutes.BASENAME);
      break;
    }
    case 'ENTERED_SEED_PHRASE': {
      try {
        const xpubs = Object.values(message.xpubs);
        const relevantBroadcasts = await query(getBroadcastsForFingerprint(xpubs[0].fingerprint));
        if (relevantBroadcasts.some((broadcast) => broadcast.status === 'SUCCESS')) {
          await clearAllData();
        }
        await query(ensureXpubsAreSaved(xpubs));

        // If the user has completed broadcasts for this set of xpubs, we should clear balances
        if (message.from === 'securityFrame') {
          void MessageBus.reply(message, { kind: 'XPUBS_SAVED' });
        }
        context.navigate(
          AppRoutes.SCANNING.replace(':fingerprint', xpubs[0].fingerprint.toString()),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        void MessageBus.reply(
          message,
          { kind: 'XPUB_SAVE_ERROR', error: errorToString(err) },
          { rejection: true },
        );
      }
      break;
    }

    // These messages are replies that are handled by the sender of the
    // original message as an awaited Promise. Nothing to do here, other than
    // convince TypeScript that we've handled all message kinds.
    case 'PONG':
    case 'READY':
    case 'SANITY_CHECK_RESPONSE':
    case 'SCANNING_PROGRESS':
    case 'CONSTRUCTED_TRANSACTIONS':
    case 'SIGNED_TRANSACTIONS':
    case 'SIGNING_ERROR':
    case 'NO_SIGNER':
    case 'QUERY_RESULT':
    case 'CONSTRUCT_TRANSACTION_ERROR':
    case 'QUERY_ERROR': {
      break;
    }
    default:
      // @ts-expect-error to guarantee we've handled all message kinds. If
      // this is broken, we missed a possible message. Hover over `message.kind`
      // below to see what was missed.
      throw new Error(`Unexpected message: ${message.kind}`);
  }
}
